import { useState, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Box,
  TablePagination,
  Typography,
} from "@mui/material";
import {
  Edit as EditIcon,
  Block as DisableIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
} from "@mui/icons-material";
import {
  getUniversalDescriptorById,
  saveUniversalDescriptor,
  disableUniversalDescriptor,
  enableUniversalDescriptor,
} from "../../api/APIHelpers";
import UniversalDescriptorEditor from "./UniversalDescriptorEditor";
import { AlertsContext } from "../../common/contexts/AlertsContext";
import { UniversalDescriptorContext } from "../../common/contexts/UniversalDescriptorContext";
import {
  exportUDDTOsToCSV,
  importUDDTOsFromCSV,
  validateImportedUDDTOs,
} from "../helpers/Helpers";
import ConfirmDialog from "../../common/components/ConfirmDialog";
import { LookupValueContext } from "../../common/contexts/LookupValueContext";
import { extractApiErrorMessage } from "../../common/helpers/ErrorUtils";

const UniversalDescriptorList = () => {
  const { universalDescriptors, refreshUniversalDescriptorContext } =
    useContext(UniversalDescriptorContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedDescriptor, setSelectedDescriptor] = useState(null);
  const { setInfoMessages } = useContext(AlertsContext);
  const lookupValues = useContext(LookupValueContext);

  // Confirmation dialog state
  const [showConfirmDeployDialog, setShowConfirmDeployDialog] = useState(false);
  const [confirmationTitle, setConfirmationTitle] = useState("");
  const [confirmationText, setConfirmationText] = useState("");
  const [confirmationOnClose, setConfirmationOnClose] = useState(null);

  const confirmationDialog = (
    <ConfirmDialog
      open={showConfirmDeployDialog}
      onClose={confirmationOnClose}
      title={confirmationTitle}
      content={confirmationText}
    />
  );

  const handleDisable = async (id) => {
    try {
      await disableUniversalDescriptor(id);
      setInfoMessages({
        type: "snackbar",
        message: "Universal Descriptor disabled successfully.",
        severity: "success",
        open: true,
      });
      refreshUniversalDescriptorContext();
    } catch (err) {
      console.error("Error disabling Universal Descriptor:", err);
      setInfoMessages({
        type: "snackbar",
        message: `Error disabling Universal Descriptor: ${extractApiErrorMessage(err)}`,
        severity: "error",
        open: true,
      });
    }
  };

  const handleEnable = async (id) => {
    try {
      await enableUniversalDescriptor(id);
      setInfoMessages({
        type: "snackbar",
        message: "Universal Descriptor enabled successfully.",
        severity: "success",
        open: true,
      });
      refreshUniversalDescriptorContext();
    } catch (err) {
      console.error("Error enabling Universal Descriptor:", err);
      setInfoMessages({
        type: "snackbar",
        message: `Error enabling Universal Descriptor: ${extractApiErrorMessage(err)}`,
        severity: "error",
        open: true,
      });
    }
  };

  const handleEdit = async (ud) => {
    const descriptor = universalDescriptors.find((item) => item.UD === ud);
    if (descriptor) {
      const fullUDDefinition = await getUniversalDescriptorById(
        descriptor.UniversalDescriptorId
      );
      setSelectedDescriptor(fullUDDefinition.data);
      setEditorOpen(true);
    }
  };

  const handleCreate = () => {
    setSelectedDescriptor({
      UniversalDescriptorId: 0,
      UD: "",
      Description: "",
      IsActive: true,
      TagName: "",
    });
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setSelectedDescriptor(null);
  };

  const handleSave = async (universalDescriptor) => {
    try {
      await saveUniversalDescriptor(universalDescriptor);
      setInfoMessages({
        type: "snackbar",
        message: "Universal Descriptor saved successfully.",
        severity: "success",
        open: true,
      });
      refreshUniversalDescriptorContext();
    } catch (err) {
      console.error("Error saving Universal Descriptor:", err);
      setInfoMessages({
        type: "snackbar",
        message: `Error saving Universal Descriptor: ${extractApiErrorMessage(err)}`,
        severity: "error",
        open: true,
      });
    }
    handleEditorClose();
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredData = universalDescriptors.filter((row) => {
    const udString = row.UD || "";
    return udString.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleExportToCSV = () => {
    // Get full DTOs for all UDs
    const dtoPromises = universalDescriptors.map((ud) =>
      getUniversalDescriptorById(ud.UniversalDescriptorId).then(
        (res) => res.data
      )
    );
    Promise.all(dtoPromises).then((dtoList) => {
      const csvContent = exportUDDTOsToCSV(dtoList, lookupValues);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "universal_descriptors.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setInfoMessages({
        type: "snackbar",
        open: true,
        severity: "success",
        message: `Successfully exported ${dtoList.length} Universal Descriptor(s) to CSV.`,
      });
    });
  };

  const handleImportFromCSV = async (text) => {
    let importedDTOs = [];
    try {
      importedDTOs = importUDDTOsFromCSV(text, lookupValues);
    } catch (err) {
      console.error("Error importing from CSV:", err);
      setInfoMessages({
        type: "dialog",
        message: `Error parsing CSV: ${err.message}`,
        severity: "error",
        title: "Import Error",
        open: true,
      });
      return;
    }
    // validate imported DTOs
    const errors = validateImportedUDDTOs(universalDescriptors, importedDTOs);
    if (errors.length > 0) {
      setInfoMessages({
        type: "dialog",
        message: errors.join("\n"),
        severity: "error",
        title: "Import Errors",
        open: true,
      });
      return;
    } else {
      const saveImportedUDs = async () => {
        // Save each imported DTO
        const savePromises = importedDTOs.map((dto) =>
          saveUniversalDescriptor(dto)
        );
        let saveErrors = [];
        const proms = await Promise.allSettled(savePromises);
        proms.forEach((result, index) => {
          if (result.status === "rejected") {
            saveErrors.push(
              `Error saving UD '${importedDTOs[index].UD}': ${extractApiErrorMessage(result.reason)}`
            );
          }
        });
        if (saveErrors.length > 0) {
          setInfoMessages({
            type: "dialog",
            message: saveErrors.join("\n"),
            severity: "error",
            title: "Save Errors",
            open: true,
          });
        } else {
          setInfoMessages({
            type: "snackbar",
            message: `Successfully imported ${importedDTOs.length} Universal Descriptor(s) from CSV.`,
            severity: "success",
            open: true,
          });
          refreshUniversalDescriptorContext();
        }
      };

      // Confirmation dialog
      const closePrompt = async (bool) => {
        setShowConfirmDeployDialog(false);
        if (bool) {
          await saveImportedUDs();
        }
      };
      setConfirmationOnClose(() => closePrompt);
      setConfirmationTitle("Confirm UD Import");
      setConfirmationText(
        <Typography>
          Are you sure you want to import the following UDs?
          {importedDTOs.map((dto) => (
            <p key={dto.UD}>{dto.UD}</p>
          ))}
        </Typography>
      );
      setShowConfirmDeployDialog(true);
    }
  };

  return (
    <>
      <Box>
        <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
          <TextField
            label='Search by Universal Descriptor'
            variant='outlined'
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            sx={{ flexGrow: 1 }}
          />
          <Button
            variant='contained'
            color='primary'
            startIcon={<AddIcon />}
            onClick={handleCreate}>
            Create New
          </Button>
          <Button
            variant='contained'
            color='primary'
            component='label'
            startIcon={<FileUploadIcon />}>
            Import from CSV
            <input
              type='file'
              onChange={(event) => {
                for (const file of event.target.files) {
                  file.text().then((text) => {
                    handleImportFromCSV(text);
                  });
                }
                event.target.value = "";
              }}
              hidden
            />
          </Button>
          <Button
            variant='contained'
            color='primary'
            startIcon={<FileDownloadIcon />}
            onClick={handleExportToCSV}>
            Export to CSV
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Universal Descriptor</TableCell>
                <TableCell align='right'>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((row) => (
                  <TableRow
                    key={row.UniversalDescriptorId}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      opacity: row.disabled ? 0.5 : 1,
                    }}>
                    <TableCell>{row.UniversalDescriptorId}</TableCell>
                    <TableCell>{row.UD}</TableCell>
                    <TableCell align='right'>
                      <Tooltip title='Edit'>
                        <span>
                          <IconButton
                            disabled={!row.IsActive}
                            size='small'
                            onClick={() => handleEdit(row.UD)}
                            color='primary'
                            sx={{ ml: 1 }}>
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {row.IsActive ? (
                        <Tooltip title='Disable'>
                          <IconButton
                            size='small'
                            onClick={() =>
                              handleDisable(row.UniversalDescriptorId)
                            }
                            color='warning'
                            sx={{ ml: 1 }}>
                            <DisableIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title='Enable'>
                          <IconButton
                            size='small'
                            onClick={() =>
                              handleEnable(row.UniversalDescriptorId)
                            }
                            color='success'
                            sx={{ ml: 1 }}>
                            <CheckIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align='center'>
                    {universalDescriptors.length === 0
                      ? "Loading..."
                      : "No results found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component='div'
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
        {selectedDescriptor && (
          <UniversalDescriptorEditor
            open={editorOpen}
            onClose={handleEditorClose}
            universalDescriptor={selectedDescriptor}
            onSave={handleSave}
          />
        )}
      </Box>
      {confirmationDialog}
    </>
  );
};

export default UniversalDescriptorList;
